import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from '@acitra/i18n';
import { useAuthStore } from '../stores/auth-store';
import { getQuickBooksAuthUrl, leaveTenant } from '../services/api';
import { LeaveTenantModal } from '../components/LeaveTenantModal';
import type { TenantWithRole } from '@acitra/shared';

export function CompanySelection() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [isConnecting, setIsConnecting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [tenantToLeave, setTenantToLeave] = useState<{ id: string; name: string } | null>(null);

  const { isAuthenticated, tenants, getConnectedTenants, fetchUserTenants, logout } =
    useAuthStore();

  // Check for error message from navigation state or URL query params
  useEffect(() => {
    // Check navigation state first
    const state = location.state as { error?: string } | null;
    if (state?.error) {
      setErrorMessage(state.error);
      // Clear the state so refresh doesn't show the error again
      window.history.replaceState({}, document.title);
      return;
    }

    // Check URL query params (used by QB callback redirects)
    const params = new URLSearchParams(location.search);
    const errorParam = params.get('error');
    if (errorParam) {
      setErrorMessage(decodeURIComponent(errorParam));
      // Clear the query param so refresh doesn't show the error again
      window.history.replaceState({}, document.title, location.pathname);
    }
  }, [location.state, location.search, location.pathname]);

  // Redirect if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Sort connected tenants by lastUsedAt (most recent first)
  const connectedTenants = getConnectedTenants().sort((a, b) => {
    const aLastUsed = (a as TenantWithRole).lastUsedAt;
    const bLastUsed = (b as TenantWithRole).lastUsedAt;
    if (!aLastUsed && !bLastUsed) return 0;
    if (!aLastUsed) return 1;
    if (!bLastUsed) return -1;
    return new Date(bLastUsed).getTime() - new Date(aLastUsed).getTime();
  });

  const disconnectedTenants = tenants.filter((t) => t.quickbooks?.disconnectedAt);

  const handleSelectTenant = (tenantId: string) => {
    // Navigate to loading screen which will validate the connection
    navigate(`/tenant/${tenantId}/loading`);
  };

  const handleReconnect = async () => {
    setIsConnecting(true);
    setErrorMessage(null);
    try {
      const authUrl = await getQuickBooksAuthUrl();
      window.location.href = authUrl;
    } catch (err) {
      console.error('Failed to get QB auth URL:', err);
      setErrorMessage('Failed to initiate QuickBooks connection.');
      setIsConnecting(false);
    }
  };

  const handleConnectQuickBooks = async () => {
    setIsConnecting(true);
    setErrorMessage(null);
    try {
      const authUrl = await getQuickBooksAuthUrl();
      window.location.href = authUrl;
    } catch (err) {
      console.error('Failed to get QB auth URL:', err);
      setErrorMessage('Failed to initiate QuickBooks connection.');
      setIsConnecting(false);
    }
  };

  const handleLeaveTenant = async () => {
    if (!tenantToLeave) return;

    await leaveTenant(tenantToLeave.id);
    // Refresh tenant list
    await fetchUserTenants();
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  const formatLastUsed = (lastUsedAt?: string) => {
    if (!lastUsedAt) return null;
    const date = new Date(lastUsedAt);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Last used: Today';
    if (diffDays === 1) return 'Last used: Yesterday';
    if (diffDays < 7) return `Last used: ${diffDays} days ago`;
    return `Last used: ${lastUsedAt.split('T')[0]}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            {t('common.appName')}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">Select a company to continue</p>
        </div>

        {/* Error Message */}
        {errorMessage && (
          <div className="rounded-md bg-red-50 p-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm font-medium text-red-800">{errorMessage}</p>
              </div>
              <div className="ml-auto pl-3">
                <button
                  onClick={() => setErrorMessage(null)}
                  className="inline-flex text-red-400 hover:text-red-500"
                >
                  <span className="sr-only">Dismiss</span>
                  <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                    <path
                      fillRule="evenodd"
                      d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Connected Companies */}
        {connectedTenants.length > 0 && (
          <div className="space-y-4">
            <p className="text-sm text-gray-500 font-medium">Connected Companies</p>
            {connectedTenants.map((tenant) => {
              const lastUsed = formatLastUsed((tenant as TenantWithRole).lastUsedAt);
              return (
                <button
                  key={tenant.id}
                  onClick={() => handleSelectTenant(tenant.id)}
                  className="w-full flex items-center justify-between p-4 border border-gray-300 rounded-lg hover:border-primary-500 hover:bg-primary-50 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
                      <span className="text-primary-600 font-semibold">
                        {tenant.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div className="text-left">
                      <div className="font-medium text-gray-900">{tenant.name}</div>
                      <div className="text-sm text-green-600 flex items-center gap-1">
                        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                            clipRule="evenodd"
                          />
                        </svg>
                        QuickBooks connected
                      </div>
                      {lastUsed && <div className="text-xs text-gray-400">{lastUsed}</div>}
                    </div>
                  </div>
                  <svg
                    className="w-5 h-5 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </button>
              );
            })}
          </div>
        )}

        {/* Disconnected Companies */}
        {disconnectedTenants.length > 0 && (
          <div className="space-y-4">
            <p className="text-sm text-gray-500 font-medium">Disconnected Companies</p>
            {disconnectedTenants.map((tenant) => (
              <div
                key={tenant.id}
                className="w-full p-4 border border-gray-200 rounded-lg bg-gray-50"
              >
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                    <span className="text-gray-500 font-semibold">
                      {tenant.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="flex-1 text-left">
                    <div className="font-medium text-gray-700">{tenant.name}</div>
                    <div className="text-sm text-yellow-600 flex items-center gap-1">
                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                        <path
                          fillRule="evenodd"
                          d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                      Disconnected
                    </div>
                  </div>
                </div>
                <div className="mt-3 flex gap-2">
                  <button
                    onClick={handleReconnect}
                    disabled={isConnecting}
                    className="flex-1 py-2 px-3 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isConnecting ? 'Connecting...' : 'Reconnect'}
                  </button>
                  <button
                    onClick={() => setTenantToLeave({ id: tenant.id, name: tenant.name })}
                    className="py-2 px-3 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                  >
                    Leave
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* No companies message */}
        {tenants.length === 0 && (
          <div className="text-center py-8">
            <div className="text-gray-500 mb-4">You don't have any companies yet.</div>
            <p className="text-sm text-gray-400 mb-6">
              Connect your QuickBooks account to get started.
            </p>
          </div>
        )}

        {/* Connect New QuickBooks Account */}
        <div className="pt-4 border-t border-gray-200">
          <button
            onClick={handleConnectQuickBooks}
            disabled={isConnecting}
            className="w-full flex items-center justify-center gap-3 py-3 px-4 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50"
          >
            {isConnecting ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-gray-700"></div>
                Connecting...
              </>
            ) : (
              <>
                <svg
                  className="w-5 h-5"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 20C7.59 20 4 16.41 4 12C4 7.59 7.59 4 12 4C16.41 4 20 7.59 20 12C20 16.41 16.41 20 12 20Z"
                    fill="#2CA01C"
                  />
                  <path d="M13 7H11V11H7V13H11V17H13V13H17V11H13V7Z" fill="#2CA01C" />
                </svg>
                Connect New QuickBooks Company
              </>
            )}
          </button>
        </div>

        <div className="text-center">
          <button onClick={handleLogout} className="text-sm text-gray-500 hover:text-gray-700">
            Sign out
          </button>
        </div>
      </div>

      {/* Leave Tenant Modal */}
      <LeaveTenantModal
        isOpen={!!tenantToLeave}
        onClose={() => setTenantToLeave(null)}
        onConfirm={handleLeaveTenant}
        tenantName={tenantToLeave?.name ?? ''}
      />
    </div>
  );
}
